import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import { SectionCard, PrimaryButton, SecondaryButton } from "@/components/ds";

// ── Plan definitions (must match WooCommerceBillingController.Plans) ──────────
const PLANS = [
  {
    id: "starter",
    name: "Velaro Starter",
    monthlyPrice: 97,
    annualPrice: 970,
    annualSavings: 194,
    trialDays: 14,
    color: "border-slate-200",
    badge: null,
    features: [
      "1 agent seat (+$19/seat)",
      "500 conversations/month",
      "200 bot auto-resolves",
      "28 WooCommerce AI tools",
      "Bot Knowledge Q&A (10 articles)",
      "Store scanning (200 pages)",
      "30-day reporting",
      "Web chat channel",
    ],
  },
  {
    id: "growth",
    name: "Velaro Growth",
    monthlyPrice: 297,
    annualPrice: 2970,
    annualSavings: 594,
    trialDays: 14,
    color: "border-blue-400",
    badge: "Most popular",
    features: [
      "3 agent seats (+$29/seat)",
      "2,000 conversations/month",
      "1,000 bot auto-resolves",
      "All 28 WooCommerce AI tools",
      "Product comparison + cart links",
      "Abandoned cart recovery",
      "500 KB articles",
      "SMS + email + WhatsApp channels",
      "90-day reporting",
    ],
  },
  {
    id: "professional",
    name: "Velaro Professional",
    monthlyPrice: 497,
    annualPrice: 4970,
    annualSavings: 994,
    trialDays: 14,
    color: "border-purple-400",
    badge: null,
    features: [
      "5 agent seats (+$39/seat)",
      "5,000 conversations/month",
      "3,000 bot auto-resolves",
      "Everything in Growth",
      "Conversation training",
      "Ticketing system (50 tickets)",
      "1-year reporting",
      "Priority support",
    ],
  },
  {
    id: "scale",
    name: "Velaro Scale",
    monthlyPrice: 997,
    annualPrice: 9970,
    annualSavings: 1994,
    trialDays: 14,
    color: "border-green-400",
    badge: null,
    features: [
      "8 agent seats (+$49/seat)",
      "15,000 conversations/month",
      "10,000 bot auto-resolves",
      "Everything in Professional",
      "AI orchestration (multi-bot)",
      "250 tickets",
      "Custom AI training",
    ],
  },
];

interface BillingStatus {
  billingStatus: string;
  contractId: string | null;
  planName: string | null;
  billingInterval: string;
  nextPaymentDate: string | null;
  contractStart: string | null;
  oauthConnected: boolean;
  hasActiveSubscription: boolean;
}

export default function WooCommerceBilling() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [interval, setIntervalMode] = useState<"monthly" | "annual">("monthly");
  const [working, setWorking] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  async function loadStatus() {
    try {
      setLoading(true);
      const data = await (await api.messaging.get("WooCommerce/billing/status")).json();
      setStatus(data);
      if (data.billingInterval === "annual") setIntervalMode("annual");
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubscribe(planId: string) {
    setWorking(true);
    try {
      const resp = await api.messaging.post("WooCommerce/billing/subscribe", {
        planId,
        billingInterval: interval,
      });
      const data = await resp.json();
      if (data.paymentUrl) {
        // Open WooCommerce.com payment page in new tab
        window.open(data.paymentUrl, "_blank", "noopener,noreferrer");
        toastAlert.displayToast(
          Severity.Info,
          "Complete your subscription in the new tab. This page will update automatically."
        );
        // Poll for status change
        setTimeout(() => loadStatus(), 8000);
      } else {
        toastAlert.displayToast(Severity.Error, data.error || "Failed to start subscription.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to connect to billing.");
    } finally {
      setWorking(false);
    }
  }

  async function handleChangePlan(planId: string) {
    setWorking(true);
    try {
      const resp = await api.messaging.post("WooCommerce/billing/change-plan", {
        planId,
        billingInterval: interval,
      });
      const data = await resp.json();
      if (data.updated) {
        toastAlert.displayToast(Severity.Success, `Plan changed to ${data.planName}.`);
        await loadStatus();
      } else if (data.paymentUrl) {
        window.open(data.paymentUrl, "_blank", "noopener,noreferrer");
        toastAlert.displayToast(Severity.Info, "Approve the plan change in the new tab.");
        setTimeout(() => loadStatus(), 8000);
      } else {
        toastAlert.displayToast(Severity.Error, data.error || "Failed to change plan.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to change plan.");
    } finally {
      setWorking(false);
    }
  }

  async function handleCancel() {
    setWorking(true);
    try {
      const resp = await api.messaging.post("WooCommerce/billing/cancel", {});
      const data = await resp.json();
      if (data.cancelled) {
        toastAlert.displayToast(Severity.Success, data.message);
        setCancelConfirm(false);
        await loadStatus();
      } else {
        toastAlert.displayToast(Severity.Error, data.error || "Failed to cancel.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to cancel subscription.");
    } finally {
      setWorking(false);
    }
  }

  const isActive = status?.billingStatus === "active";
  const currentPlanId = status?.planName?.replace("-annual", "") ?? null;

  if (loading) return <div className="p-8 text-center text-slate-400 text-sm">Loading billing status...</div>;

  return (
    <div className="space-y-5">
      {/* Commission notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        <strong>WooCommerce.com marketplace billing</strong> — Plans are billed through WooCommerce.com.
        Clicking a plan opens WooCommerce.com's payment page in a new tab.
        Prices shown are what you pay; Velaro receives the net after the marketplace commission.
      </div>

      {/* Current plan banner */}
      {isActive && currentPlanId && (
        <SectionCard title="Current Plan">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-medium text-slate-500 mb-1">Current plan</div>
              <div className="text-lg font-bold text-slate-800">
                {PLANS.find((p) => p.id === currentPlanId)?.name ?? currentPlanId}
              </div>
              <div className="text-sm text-slate-500 mt-0.5">
                {status?.billingInterval === "annual" ? "Annual billing" : "Monthly billing"}
                {status?.nextPaymentDate && (
                  <> · Renews {new Date(status.nextPaymentDate).toLocaleDateString()}</>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                Active
              </span>
              {!cancelConfirm && (
                <button
                  type="button"
                  onClick={() => setCancelConfirm(true)}
                  className="text-xs text-slate-400 hover:text-red-500 transition"
                >
                  Cancel subscription
                </button>
              )}
            </div>
          </div>
          {cancelConfirm && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 mb-3">
                Cancelling stops future renewals. You keep access until the end of the current period.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={working}
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {working ? "Cancelling..." : "Yes, cancel"}
                </button>
                <SecondaryButton onClick={() => setCancelConfirm(false)}>Keep subscription</SecondaryButton>
              </div>
            </div>
          )}
        </SectionCard>
      )}

      {/* Billing interval toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-slate-700">Billing interval</span>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {(["monthly", "annual"] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setIntervalMode(opt)}
              className={`px-3 py-1 text-sm rounded-md transition ${
                interval === opt ? "bg-white shadow text-slate-800 font-medium" : "text-slate-500"
              }`}
            >
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
              {opt === "annual" && <span className="ml-1.5 text-xs text-green-600 font-medium">2 months free</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {PLANS.map((plan) => {
          const price = interval === "annual" ? plan.annualPrice : plan.monthlyPrice;
          const isCurrent = currentPlanId === plan.id && isActive;
          return (
            <div
              key={plan.id}
              className={`bg-white rounded-2xl border-2 p-5 flex flex-col ${
                isCurrent ? "border-blue-500" : plan.color
              }`}
            >
              {plan.badge && (
                <div className="text-xs font-semibold text-blue-600 bg-blue-50 rounded-full px-2 py-0.5 self-start mb-2">
                  {plan.badge}
                </div>
              )}
              {isCurrent && (
                <div className="text-xs font-semibold text-blue-600 bg-blue-50 rounded-full px-2 py-0.5 self-start mb-2">
                  Current plan
                </div>
              )}
              <div className="font-semibold text-slate-800 mb-1">{plan.name}</div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-2xl font-bold text-slate-900">${price.toLocaleString()}</span>
                <span className="text-sm text-slate-500">/{interval === "annual" ? "yr" : "mo"}</span>
              </div>
              {interval === "annual" && (
                <div className="text-xs text-green-600 font-medium mb-3">
                  Save ${plan.annualSavings}/yr vs monthly
                </div>
              )}
              {plan.trialDays > 0 && !isActive && (
                <div className="text-xs text-slate-500 mb-3">{plan.trialDays}-day free trial</div>
              )}
              <ul className="flex-1 space-y-1.5 mb-4">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <svg className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" viewBox="0 0 16 16" fill="currentColor">
                      <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
                    </svg>
                    <span className="text-xs text-slate-600">{f}</span>
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <button
                  type="button"
                  disabled
                  className="w-full py-2 text-sm font-medium rounded-lg bg-slate-100 text-slate-400 cursor-not-allowed"
                >
                  Current plan
                </button>
              ) : isActive ? (
                <PrimaryButton disabled={working} onClick={() => handleChangePlan(plan.id)} className="w-full">
                  {working ? "Processing..." : "Switch to this plan"}
                </PrimaryButton>
              ) : (
                <PrimaryButton disabled={working} onClick={() => handleSubscribe(plan.id)} className="w-full">
                  {working ? "Processing..." : plan.trialDays > 0 ? `Start ${plan.trialDays}-day trial` : "Subscribe"}
                </PrimaryButton>
              )}
            </div>
          );
        })}
      </div>

      {/* Pricing note */}
      <div className="text-xs text-slate-400 text-center">
        All prices in USD. Billed by WooCommerce.com · Annual billing saves 2 months vs monthly.
        WooCommerce.com marketplace commission applies. Cancel anytime — access continues through the current period.
      </div>
    </div>
  );
}
